import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Grid } from 'components/ui';
import TextInput from 'components/Inputs/TextInput/TextInput';
import FormValidator from 'utils/FormValidator';
import ErrorConstants from 'utils/ErrorConstants';
import ErrorsUtils from 'utils/Errors';
import localeUtils from 'utils/LanguageLocale';
import { mediaQueries } from 'style/config';
import helperUtils from 'utils/Helpers';
const { sanitizeInput } = helperUtils;

const GRID_GUTTER = 4;

function NameFields({
    firstName = '',
    lastName = '',
    isFirstNameFieldDisabled = false,
    isLastNameFieldDisabled = false,
    isAdsRestricted = false,
    isFCFinalReleaseEnabled = false,
    isSubscription = false,
    modalTitle = '',
    localization = {},
    onFirstNameChange,
    onLastNameChange,
    onFirstNameBlur,
    onLastNameBlur,
    firstNameRef,
    lastNameRef,
    fireFormErrorAnalytics,
    forceSingleColumn
}) {
    const { firstNameText = 'First Name', lastNameText = 'Last Name' } = localization;
    const isUS = localeUtils.isUS();

    const firstNameMaxLength = isAdsRestricted ? FormValidator.FIELD_LENGTHS.firstNameAdsRestricted : FormValidator.FIELD_LENGTHS.name;

    const lastNameMaxLength = isAdsRestricted ? FormValidator.FIELD_LENGTHS.lastNameAdsRestricted : FormValidator.FIELD_LENGTHS.name;

    const handleInputSanitization = (e, onChange) => {
        const value = sanitizeInput(e.target.value);
        const fieldName = e.target.name;

        // Update the input value
        e.target.value = value;

        if (onChange) {
            onChange(fieldName, value);
        }
    };

    const validateFirstName = firstNameValue => {
        if (FormValidator.isEmpty(firstNameValue)) {
            if (isSubscription && fireFormErrorAnalytics) {
                fireFormErrorAnalytics(modalTitle, ErrorsUtils.getMessage(ErrorConstants.ERROR_CODES.FIRST_NAME));
            }

            return ErrorConstants.ERROR_CODES.FIRST_NAME;
        }

        if (!FormValidator.isValidLength(firstNameValue, 1, firstNameMaxLength)) {
            if (isSubscription && fireFormErrorAnalytics) {
                const errorCode = isAdsRestricted ? ErrorConstants.ERROR_CODES.FIRST_NAME_ADS_LONG : ErrorConstants.ERROR_CODES.FIRST_NAME_LONG;
                fireFormErrorAnalytics(modalTitle, ErrorsUtils.getMessage(errorCode));
            }

            return isAdsRestricted ? ErrorConstants.ERROR_CODES.FIRST_NAME_ADS_LONG : ErrorConstants.ERROR_CODES.FIRST_NAME_LONG;
        }

        return null;
    };

    const validateLastName = lastNameValue => {
        if (FormValidator.isEmpty(lastNameValue)) {
            if (isSubscription && fireFormErrorAnalytics) {
                fireFormErrorAnalytics(modalTitle, ErrorsUtils.getMessage(ErrorConstants.ERROR_CODES.LAST_NAME));
            }

            return ErrorConstants.ERROR_CODES.LAST_NAME;
        }

        if (!FormValidator.isValidLength(lastNameValue, 1, lastNameMaxLength)) {
            if (isSubscription && fireFormErrorAnalytics) {
                const errorCode = isAdsRestricted ? ErrorConstants.ERROR_CODES.LAST_NAME_ADS_LONG : ErrorConstants.ERROR_CODES.LAST_NAME_LONG;
                fireFormErrorAnalytics(modalTitle, ErrorsUtils.getMessage(errorCode));
            }

            return isAdsRestricted ? ErrorConstants.ERROR_CODES.LAST_NAME_ADS_LONG : ErrorConstants.ERROR_CODES.LAST_NAME_LONG;
        }

        return null;
    };

    return (
        <Grid
            gap={forceSingleColumn ? 0 : [0, GRID_GUTTER]}
            gridTemplateColumns={forceSingleColumn ? '1fr' : '1fr 1fr'}
        >
            <div css={styles.fullField}>
                <TextInput
                    name='firstName'
                    label={firstNameText}
                    autoComplete='given-name'
                    autoCorrect='off'
                    required={true}
                    hideAsterisk={isUS || isFCFinalReleaseEnabled}
                    maxLength={firstNameMaxLength}
                    disabled={isFirstNameFieldDisabled}
                    value={firstName}
                    ref={firstNameRef}
                    onChange={e => handleInputSanitization(e, onFirstNameChange)}
                    onKeyUp={e => handleInputSanitization(e, onFirstNameChange)}
                    onBlur={onFirstNameBlur}
                    validateError={validateFirstName}
                    data-at={Sephora.debug.dataAt('first_name_input')}
                    dataAtError={Sephora.debug.dataAt('first_name_required_error')}
                />
            </div>
            <div css={styles.fullField}>
                <TextInput
                    name='lastName'
                    label={lastNameText}
                    autoComplete='family-name'
                    autoCorrect='off'
                    required={true}
                    hideAsterisk={isUS || isFCFinalReleaseEnabled}
                    maxLength={lastNameMaxLength}
                    disabled={isLastNameFieldDisabled}
                    value={lastName}
                    ref={lastNameRef}
                    onChange={e => handleInputSanitization(e, onLastNameChange)}
                    onKeyUp={e => handleInputSanitization(e, onLastNameChange)}
                    onBlur={onLastNameBlur}
                    validateError={validateLastName}
                    data-at={Sephora.debug.dataAt('last_name_input')}
                    dataAtError={Sephora.debug.dataAt('last_name_required_error')}
                />
            </div>
        </Grid>
    );
}

const styles = {
    fullField: {
        [mediaQueries.xsMax]: {
            gridColumn: '1 / -1'
        }
    }
};

export default wrapFunctionalComponent(NameFields, 'NameFields');
